/**
 * How an EoT claim is built, start to finish — the reading tab of the builder.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Settings2, Scale, CalendarClock, Activity, GitBranch, Calculator,
  FileText, ClipboardCheck, ShieldCheck, Sparkles, RefreshCw, Link2,
} from 'lucide-react';

const STEPS = [
  {
    icon: Settings2, tab: 'Setup',
    title: '1 · Set the claim up and point it at the evidence',
    body: 'Name the claim, pick the contract, the claim type and who is claiming. Set the '
      + 'contract start, the original completion date and any extension already granted — every '
      + 'later figure is measured from those. Tick the contract documents and the programmes '
      + '(engineering, procurement, construction, pre-commissioning) the claim should read.',
    points: [
      'Pull selected documents into the claim so the analysis has something to read.',
      '“Read the dates from the documents” fills the contract dates from the contract itself.',
      'Linked schedules are what the deterministic analysis walks — without one, only the narrative is available.',
    ],
  },
  {
    icon: Scale, tab: 'Time Rules',
    title: '2 · Extract the time rules from the contract',
    body: 'Cognara reads the linked documents and lists the clauses that govern time: notice '
      + 'periods, particulars, the prescribed delay analysis method, programme submission, '
      + 'liquidated damages and prolongation entitlement — each quoted verbatim with its clause '
      + 'reference.',
    points: [
      'Accept, edit or reject each rule. Nothing is used until you have decided on it.',
      'Lock the rules once agreed; the locked set is what the claim is judged against.',
      'Already-read documents are reused, so the analysis does not pay to read them twice. Rescan when a document changes.',
    ],
  },
  {
    icon: CalendarClock, tab: 'Events',
    title: '3 · Find and confirm the delay events',
    body: 'Each event is a dated cause of delay — a late drawing, a variation, an access '
      + 'restriction, adverse weather — with the affected activity, the evidence it came from '
      + 'and whether it is an employer or contractor risk.',
    points: [
      'Candidates come from the documents; you confirm, edit or reject each one.',
      'Add an event by hand where the paperwork does not say it plainly.',
      'Notice compliance is checked against the locked rules: compliant, late or missing.',
    ],
  },
  {
    icon: Activity, tab: 'Schedule Health',
    title: '4 · Check the programme can carry the claim',
    body: 'Open ends, dangling logic, negative lag, excessive constraints and out-of-sequence '
      + 'progress are listed before any analysis runs. A programme that fails here produces a '
      + 'critical path that will not survive cross-examination.',
    points: ['Fix or acknowledge each finding — the report records which were accepted as-is.'],
  },
  {
    icon: GitBranch, tab: 'Analysis',
    title: '5 · Run the delay analysis',
    body: 'The chosen method is computed on the linked programme, not written by a model: time '
      + 'impact analysis, as-planned v as-built, collapsed as-built or time slice. Concurrency '
      + 'is applied by the rule set on the claim (Malmaison, dominant cause or apportionment).',
    points: [
      'Every run keeps its inputs, so two runs can be compared and the difference explained.',
      'The critical path and the days of entitlement come out of the calculation, not the narrative.',
    ],
  },
  {
    icon: Calculator, tab: 'Entitlement',
    title: '6 · Price the entitlement',
    body: 'Extension days convert into money: prolongation on an actual-cost, formula (Hudson, '
      + 'Emden, Eichleay) or agreed-rate basis, plus disruption and any liquidated damages '
      + 'relieved by the extension.',
    points: [
      'Actual-cost prolongation draws the project’s own cost transactions over the delay window.',
      'The LD position comes from the tracker, so relief and exposure stay consistent.',
    ],
  },
  {
    icon: FileText, tab: 'Claim Pack',
    title: '7 · Assemble the submission',
    body: 'A numbered pack: the contractual basis, the chronology, the event-by-event '
      + 'entitlement, the analysis appendix and the evidence schedule — exported as one '
      + 'document with the evidence referenced by its document id.',
    points: ['Drafted sections are editable; the figures are read from the analysis and cannot drift.'],
  },
  {
    icon: ClipboardCheck, tab: 'Review & Tracking',
    title: '8 · Issue, track and sync back',
    body: 'Record submission, the engineer’s response, any determination and the days finally '
      + 'awarded. The result is pushed back to the main claims register, so the register shows '
      + 'the days and the money being claimed against that contract.',
    points: [
      'Status moves draft → submitted → under review → determined, each with a date and who did it.',
      'Sync to the register is one action and can be repeated as the position changes.',
    ],
  },
];

const NOTES = [
  { icon: Sparkles, title: 'What the AI does and does not do',
    body: 'Cognara extracts clauses, suggests events and drafts prose. It never decides a day or '
      + 'a rupee — entitlement is computed from the programme, the rules you locked and the '
      + 'events you confirmed.' },
  { icon: RefreshCw, title: 'Reading documents once',
    body: 'Document readings are cached per document. Time Rules and Events reuse them, and both '
      + 'show live progress with an elapsed time and an estimate while a pass runs. Rescan only '
      + 'when the underlying document has changed.' },
  { icon: Link2, title: 'Where the claim shows up elsewhere',
    body: 'The contract’s claims register carries the synced days and value; the LD tracker '
      + 'carries any relief; the programme carries the impacted activities.' },
  { icon: ShieldCheck, title: 'What travels if you move the platform',
    body: 'A migration bundle carries the claim, its locked rules, confirmed events, programmes, '
      + 'analysis runs, entitlement, packs, tracking history and every uploaded piece of '
      + 'evidence — project-scoped bundles carry only the claims of the projects you tick.' },
];

export default function EoTGuideTab() {
  return (
    <div className="space-y-4 pt-4" data-testid="eot-guide-tab">
      <Card className="bg-muted/40 border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-foreground text-base" data-testid="eot-guide-title">
            How an extension of time claim is built here
          </CardTitle>
          <CardDescription className="text-[11px]">
            Eight steps, in order. Each one leaves a record of what was decided and by whom, so the
            claim can be defended line by line.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="space-y-3">
        {STEPS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.tab} className="rounded-lg border border-border p-4 bg-card"
                 data-testid={`eot-guide-step-${s.tab.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-md bg-muted p-2 shrink-0">
                  <Icon className="w-4 h-4 text-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{s.title}</p>
                    <Badge variant="outline" className="text-[9px]">{s.tab} tab</Badge>
                  </div>
                  <p className="text-[12px] text-muted-foreground mt-1.5 leading-relaxed">{s.body}</p>
                  <ul className="mt-2 space-y-1">
                    {s.points.map((p, i) => (
                      <li key={i} className="text-[11px] text-muted-foreground flex gap-2">
                        <span className="text-foreground/50">—</span><span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {NOTES.map((n) => {
          const Icon = n.icon;
          return (
            <div key={n.title} className="rounded-lg border border-border p-4 bg-muted/30"
                 data-testid={`eot-guide-note-${n.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 text-foreground" />
                <p className="text-[12px] font-semibold text-foreground">{n.title}</p>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5 leading-relaxed">{n.body}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
